/**
 * FieldReportDrawer Component
 * Create / Edit Field Report - large drawer (multi-section)
 * BuilderBel Construction Project Management System
 */

import { AlertCircle, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, Dropdown, FormSwitch, Input, NumberInput, TextArea } from '../forms';
import type {
  CreateFieldReportRequest,
  FieldReport,
  FieldReportIssue,
  FieldReportPriority,
  FieldReportType,
  ReportSection,
  SiteConditions,
  WeatherInfo,
  WorkProgress,
} from '../../types/fieldReport';
import { useCreateFieldReportMutation, useUpdateFieldReportMutation } from '../../services/fieldReportApi';
import { useToast } from '../../contexts/ToastContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const REPORT_TYPE_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'progress', label: 'Progress' },
  { value: 'observation', label: 'Observation' },
  { value: 'issue', label: 'Issue' },
  { value: 'site_condition', label: 'Site Condition' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const ISSUE_SEVERITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'major', label: 'Major' },
  { value: 'critical', label: 'Critical' },
];

const ISSUE_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
];

const TEMP_UNIT_OPTIONS = [
  { value: 'F', label: '°F' },
  { value: 'C', label: '°C' },
];

// ============================================================================
// HELPERS
// ============================================================================

const formatDateForInput = (dateStr?: string): string => {
  if (!dateStr) return new Date().toISOString().split('T')[0];
  return new Date(dateStr).toISOString().split('T')[0];
};

// ============================================================================
// PROPS
// ============================================================================

interface FieldReportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  report?: FieldReport | null;
}

// ============================================================================
// EMPTY DEFAULTS
// ============================================================================

const emptySection = (): ReportSection => ({
  title: '',
  content: '',
  photos: [],
  order: 0,
});

const emptyWorkProgress = (): WorkProgress => ({
  activity: '',
  description: '',
  percentComplete: undefined,
  notes: '',
});

const emptyIssue = (): FieldReportIssue => ({
  description: '',
  severity: 'minor',
  status: 'open',
  assignedTo: '',
  resolution: '',
});

const emptyWeather = (): WeatherInfo => ({
  condition: '',
  temperature: undefined,
  temperatureUnit: 'F',
  humidity: undefined,
  windSpeed: undefined,
  precipitation: false,
});

const emptySiteConditions = (): SiteConditions => ({
  groundCondition: '',
  accessCondition: '',
  safetyHazards: '',
  environmentalIssues: '',
});

// ============================================================================
// COMPONENT
// ============================================================================

const FieldReportDrawer: React.FC<FieldReportDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  report,
}) => {
  const toast = useToast();
  const isEditing = !!report;

  // Mutations
  const [createReport, { isLoading: isCreating }] = useCreateFieldReportMutation();
  const [updateReport, { isLoading: isUpdating }] = useUpdateFieldReportMutation();
  const isSaving = isCreating || isUpdating;

  // ---- Form State ----
  const [reportDate, setReportDate] = useState<string>(formatDateForInput(report?.reportDate));
  const [title, setTitle] = useState<string>(report?.title || '');
  const [reportType, setReportType] = useState<FieldReportType>(report?.reportType || 'general');
  const [priority, setPriority] = useState<FieldReportPriority>(report?.priority || 'medium');
  const [preparedByName, setPreparedByName] = useState<string>(report?.preparedByName || '');
  const [location, setLocation] = useState<string>(report?.location || '');
  const [area, setArea] = useState<string>(report?.area || '');
  const [notes, setNotes] = useState<string>(report?.notes || '');

  // Sub-sections
  const [weather, setWeather] = useState<WeatherInfo>(report?.weather || emptyWeather());
  const [sections, setSections] = useState<ReportSection[]>(
    report?.sections?.length ? report.sections : []
  );
  const [workProgress, setWorkProgress] = useState<WorkProgress[]>(
    report?.workProgress?.length ? report.workProgress : []
  );
  const [issues, setIssues] = useState<FieldReportIssue[]>(
    report?.issues?.length ? report.issues : []
  );
  const [siteConditions, setSiteConditions] = useState<SiteConditions>(
    report?.siteConditions || emptySiteConditions()
  );

  // Collapsed sections
  const [showWeather, setShowWeather] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [showWorkProgress, setShowWorkProgress] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [showSiteConditions, setShowSiteConditions] = useState(false);

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when report changes or drawer opens
  useEffect(() => {
    if (isOpen) {
      setReportDate(formatDateForInput(report?.reportDate));
      setTitle(report?.title || '');
      setReportType(report?.reportType || 'general');
      setPriority(report?.priority || 'medium');
      setPreparedByName(report?.preparedByName || '');
      setLocation(report?.location || '');
      setArea(report?.area || '');
      setNotes(report?.notes || '');
      setWeather(report?.weather || emptyWeather());
      setSections(report?.sections?.length ? report.sections : []);
      setWorkProgress(report?.workProgress?.length ? report.workProgress : []);
      setIssues(report?.issues?.length ? report.issues : []);
      setSiteConditions(report?.siteConditions || emptySiteConditions());
      setErrors({});
    }
  }, [isOpen, report]);

  // --------------------------------------------------------------------------
  // VALIDATION
  // --------------------------------------------------------------------------

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!reportDate) newErrors.reportDate = 'Report date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --------------------------------------------------------------------------
  // SUBMIT
  // --------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload: CreateFieldReportRequest = {
      reportDate,
      title: title.trim(),
      reportType,
      priority,
      preparedByName: preparedByName.trim(),
      location: location.trim(),
      area: area.trim(),
      notes: notes.trim(),
      weather: weather.condition || weather.temperature !== undefined ? weather : undefined,
      sections,
      workProgress,
      issues,
      siteConditions:
        siteConditions.groundCondition ||
        siteConditions.accessCondition ||
        siteConditions.safetyHazards ||
        siteConditions.environmentalIssues
          ? siteConditions
          : undefined,
    };

    try {
      if (isEditing && report) {
        await updateReport({ projectId, reportId: report._id, data: payload }).unwrap();
        toast.success('Field report updated successfully');
      } else {
        await createReport({ projectId, data: payload }).unwrap();
        toast.success('Field report created successfully');
      }
      onClose();
    } catch {
      toast.error(isEditing ? 'Failed to update field report' : 'Failed to create field report');
    }
  };

  // --------------------------------------------------------------------------
  // SECTIONS HANDLERS
  // --------------------------------------------------------------------------

  const addSection = () => setSections(prev => [...prev, { ...emptySection(), order: prev.length }]);
  const removeSection = (idx: number) => setSections(prev => prev.filter((_, i) => i !== idx));
  const updateSection = (idx: number, key: keyof ReportSection, val: string | number) => {
    setSections(prev => prev.map((s, i) => (i === idx ? { ...s, [key]: val } : s)));
  };

  const addWorkProgress = () => setWorkProgress(prev => [...prev, emptyWorkProgress()]);
  const removeWorkProgress = (idx: number) =>
    setWorkProgress(prev => prev.filter((_, i) => i !== idx));
  const updateWorkProgress = (
    idx: number,
    key: keyof WorkProgress,
    val: string | number | undefined
  ) => {
    setWorkProgress(prev => prev.map((w, i) => (i === idx ? { ...w, [key]: val } : w)));
  };

  const addIssue = () => setIssues(prev => [...prev, emptyIssue()]);
  const removeIssue = (idx: number) => setIssues(prev => prev.filter((_, i) => i !== idx));
  const updateIssue = (idx: number, key: keyof FieldReportIssue, val: string) => {
    setIssues(prev => prev.map((issue, i) => (i === idx ? { ...issue, [key]: val } : issue)));
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  if (!isOpen) return null;

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - large (5+ fields, multiple sections) */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right duration-200">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Field Report' : 'Create Field Report'}
              </h2>
              {isEditing && report && (
                <p className="text-sm text-gray-500 mt-0.5">{report.reportNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* ---- BASIC INFORMATION ---- */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Title */}
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="Enter report title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* Report Date */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Report Date <span className="text-red-500">*</span>
                  </label>
                  <DatePicker value={reportDate} onChange={setReportDate} />
                  {errors.reportDate && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.reportDate}
                    </p>
                  )}
                </div>

                {/* Report Type */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Report Type</label>
                  <Dropdown
                    options={REPORT_TYPE_OPTIONS}
                    value={reportType}
                    onChange={val => setReportType(val as FieldReportType)}
                    placeholder="Select type"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Priority</label>
                  <Dropdown
                    options={PRIORITY_OPTIONS}
                    value={priority}
                    onChange={val => setPriority(val as FieldReportPriority)}
                    placeholder="Select priority"
                  />
                </div>

                {/* Prepared By */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Prepared By</label>
                  <Input
                    value={preparedByName}
                    onChange={e => setPreparedByName(e.target.value)}
                    placeholder="Name of preparer"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Location</label>
                  <Input
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    placeholder="Site location"
                  />
                </div>

                {/* Area */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Area</label>
                  <Input
                    value={area}
                    onChange={e => setArea(e.target.value)}
                    placeholder="Work area"
                  />
                </div>

                {/* Notes */}
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Notes</label>
                  <TextArea
                    value={notes}
                    onChange={setNotes}
                    placeholder="General notes and observations"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* ---- WEATHER (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowWeather(v => !v)}
              >
                <span>Weather Conditions</span>
                {showWeather ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showWeather && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">Condition</label>
                    <Input
                      value={weather.condition || ''}
                      onChange={e => setWeather(w => ({ ...w, condition: e.target.value }))}
                      placeholder="e.g. Sunny, Cloudy, Rain"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Temperature</label>
                    <NumberInput
                      value={weather.temperature}
                      onChange={val => setWeather(w => ({ ...w, temperature: val }))}
                      placeholder="Temp"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Unit</label>
                    <Dropdown
                      options={TEMP_UNIT_OPTIONS}
                      value={weather.temperatureUnit || 'F'}
                      onChange={val =>
                        setWeather(w => ({ ...w, temperatureUnit: val as 'F' | 'C' }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Humidity (%)</label>
                    <NumberInput
                      value={weather.humidity}
                      onChange={val => setWeather(w => ({ ...w, humidity: val }))}
                      placeholder="0–100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Wind Speed</label>
                    <NumberInput
                      value={weather.windSpeed}
                      onChange={val => setWeather(w => ({ ...w, windSpeed: val }))}
                      placeholder="mph / km/h"
                    />
                  </div>
                  <div className="mt-5">
                    <FormSwitch
                      id="precipitation"
                      label="Precipitation"
                      checked={weather.precipitation || false}
                      onChange={checked => setWeather(w => ({ ...w, precipitation: checked }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* ---- REPORT SECTIONS (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowSections(v => !v)}
              >
                <span>Report Sections ({sections.length})</span>
                {showSections ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showSections && (
                <div className="space-y-4">
                  {sections.map((section, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 relative">
                      <button
                        type="button"
                        onClick={() => removeSection(idx)}
                        className="absolute top-3 right-3 p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                        aria-label="Remove section"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Section Title <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={section.title}
                            onChange={e => updateSection(idx, 'title', e.target.value)}
                            placeholder="Section title"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Content <span className="text-red-500">*</span>
                          </label>
                          <TextArea
                            value={section.content}
                            onChange={val => updateSection(idx, 'content', val)}
                            placeholder="Section content and observations"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addSection}
                    className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add Section
                  </button>
                </div>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* ---- WORK PROGRESS (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowWorkProgress(v => !v)}
              >
                <span>Work Progress ({workProgress.length})</span>
                {showWorkProgress ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showWorkProgress && (
                <div className="space-y-4">
                  {workProgress.map((wp, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 relative">
                      <button
                        type="button"
                        onClick={() => removeWorkProgress(idx)}
                        className="absolute top-3 right-3 p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                        aria-label="Remove work progress entry"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">
                            Activity <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={wp.activity}
                            onChange={e => updateWorkProgress(idx, 'activity', e.target.value)}
                            placeholder="Activity name"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Description</label>
                          <TextArea
                            value={wp.description || ''}
                            onChange={val => updateWorkProgress(idx, 'description', val)}
                            placeholder="Description of work"
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            % Complete
                          </label>
                          <NumberInput
                            value={wp.percentComplete}
                            onChange={val => updateWorkProgress(idx, 'percentComplete', val)}
                            placeholder="0–100"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Notes</label>
                          <Input
                            value={wp.notes || ''}
                            onChange={e => updateWorkProgress(idx, 'notes', e.target.value)}
                            placeholder="Additional notes"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addWorkProgress}
                    className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add Work Progress Entry
                  </button>
                </div>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* ---- ISSUES (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowIssues(v => !v)}
              >
                <span>Issues ({issues.length})</span>
                {showIssues ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showIssues && (
                <div className="space-y-4">
                  {issues.map((issue, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 relative">
                      <button
                        type="button"
                        onClick={() => removeIssue(idx)}
                        className="absolute top-3 right-3 p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                        aria-label="Remove issue"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <TextArea
                            value={issue.description}
                            onChange={val => updateIssue(idx, 'description', val)}
                            placeholder="Describe the issue"
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Severity</label>
                          <Dropdown
                            options={ISSUE_SEVERITY_OPTIONS}
                            value={issue.severity}
                            onChange={val => updateIssue(idx, 'severity', val)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Status</label>
                          <Dropdown
                            options={ISSUE_STATUS_OPTIONS}
                            value={issue.status}
                            onChange={val => updateIssue(idx, 'status', val)}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Assigned To</label>
                          <Input
                            value={issue.assignedTo || ''}
                            onChange={e => updateIssue(idx, 'assignedTo', e.target.value)}
                            placeholder="Name or role"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Due Date</label>
                          <DatePicker
                            value={issue.dueDate || ''}
                            onChange={val => updateIssue(idx, 'dueDate', val)}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Resolution</label>
                          <TextArea
                            value={issue.resolution || ''}
                            onChange={val => updateIssue(idx, 'resolution', val)}
                            placeholder="Resolution details"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addIssue}
                    className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add Issue
                  </button>
                </div>
              )}
            </div>

            <hr className="border-gray-100" />

            {/* ---- SITE CONDITIONS (collapsible) ---- */}
            <div>
              <button
                type="button"
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
                onClick={() => setShowSiteConditions(v => !v)}
              >
                <span>Site Conditions</span>
                {showSiteConditions ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showSiteConditions && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Ground Condition</label>
                    <TextArea
                      value={siteConditions.groundCondition || ''}
                      onChange={val => setSiteConditions(s => ({ ...s, groundCondition: val }))}
                      placeholder="Describe ground conditions"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Access Condition</label>
                    <TextArea
                      value={siteConditions.accessCondition || ''}
                      onChange={val => setSiteConditions(s => ({ ...s, accessCondition: val }))}
                      placeholder="Describe access conditions"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Safety Hazards</label>
                    <TextArea
                      value={siteConditions.safetyHazards || ''}
                      onChange={val => setSiteConditions(s => ({ ...s, safetyHazards: val }))}
                      placeholder="List any safety hazards identified"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Environmental Issues
                    </label>
                    <TextArea
                      value={siteConditions.environmentalIssues || ''}
                      onChange={val =>
                        setSiteConditions(s => ({ ...s, environmentalIssues: val }))
                      }
                      placeholder="Describe environmental issues"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex justify-end space-x-3 flex-shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Saving...
                </>
              ) : isEditing ? (
                'Update Report'
              ) : (
                'Create Report'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default FieldReportDrawer;
